<template>
	<div class="content-page content-page--vendors-history">
		<ActionSetNav
			v-if="!loading"
			:name="this.$route.params.name"
			:display="true"
			:actions="actions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					History
					<span class="content-page__title--detail"
						>- {{ name }}</span
					>
				</h2>
			</div>
		</div>
		<div class="results-toolbar">
			<HistoryFilters
				v-model="filters"
				:placeHolder="'Search by addition or update'"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
			/>
			<div class="results-toolbar__counter" v-if="resultCounter">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<ScrollContainer v-if="!loading && hasResults">
			<Table
				class="table-container--vendors-history"
				:sourceObject="tableSource"
				:isArrowUp="true"
				:sortOptions="handleSort"
				@change="updatedPagination"
				@sort="updateDirection"
			>
				<template #col-group>
					<colgroup>
						<!-- Date/Time -->
						<col class="table__col table__col--date-time" />

						<!-- Action -->
						<col class="table__col table__col--action" />

						<!-- Changed By - Role -->
						<col class="table__col table__col--changed-by" />
					</colgroup>
				</template>
				<template #body-timestamp="{ body }">
					<div class="vendors-history-data">
						{{ fullDateString(body.timestamp) }}
						<div class="vendors-history-data__text-emphasis">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-action="{ body, index }">
					<span
						class="formHistoy-log-text"
						v-html="formatActionText(body, 1)"
					></span>
					<Button
						v-if="
							body.fieldChanges.items &&
								body.fieldChanges.items.length > 1
						"
						className="btn--link"
						labelText="Details"
						data-testid="rowDetailsLink"
						:btnClick="() => showRowDetail(index)"
					/>
					<Button
						v-if="isDeleteOrCreate(body)"
						className="btn--link"
						labelText="Details"
						:btnClick="() => displayDetailsModal(body)"
					/>
				</template>
				<template #body-changedByAndRole="{body}">
					<span
						class="formHistoy-log-text"
						v-html="formatChangeByRole(body)"
					></span>
				</template>
			</Table>
			<LogHistoryDetailModal
				v-if="displayRowDetail"
				@close="displayRowDetail = false"
				:display="displayRowDetail"
				title="Details"
				:logs="fieldChanges"
				data-test="logHistoryDetailModal"
			/>
			<LogHistoryObjDetailModal
				:objectDetails="objectDetails"
				v-if="showObjDetailsModal"
				@close="showObjDetailsModal = false"
				data-test="logHistoryObjDetailModal"
			/>
		</ScrollContainer>
		<NoResults v-show="!resultCounter && !loading" data-test="noResults" />
	</div>
</template>

<script>
//components
import ScrollContainer from '@/components/shells/scroll-container/scroll-container.vue';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs.vue';
import Button from '@/components/forms/button/button';
import NoResults from '@/site-components/no-results/no-results.vue';
import ResultCounter from '@/site-components/result-counter/result-counter.vue';
import Spinner from '@/components/visual/spinner/spinner.vue';
import Table from '@/components/shells/table/table';
import HistoryFilters from '@/sections/vendors/history/vendors-history--filters.vue';
import LogHistoryDetailModal from '@/sections/vendors/history/log-history-detail-modal.vue';
import LogHistoryObjDetailModal from '@/views/shared/log-history-obj-detail-modal';
import ActionSetNav from '@/site-components/actions-set/action-set-nav.vue';
//mixins
import HistoryHelpers from '@/mixins/history-helpers';

//utils
import { mapActions, mapState } from 'vuex';
import IntegrationActions from '@/sections/vendors/integrations/history/integrations-actions.js';
import _ from 'lodash';
import { sub } from 'date-fns';
import { formatDate } from '@/utils/datetime';
import DatetimeMixin from '@/mixins/datetime-mixin';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';
import PageKeyNames from '@/utils/page-key-names';

//constants
const entityTypes = ['Integrators']; //Dev change Integrators by Integrations after Integrations Logs is ready

export default {
	name: 'vendors-integrations-history',
	mixins: [
		IntegrationActions,
		HistoryHelpers,
		DatetimeMixin
	],
	components: {
		ScrollContainer,
		HistoryFilters,
		Breadcrumbs,
		Button,
		LogHistoryDetailModal,
		LogHistoryObjDetailModal,
		ResultCounter,
		NoResults,
		Spinner,
		Table,
		ActionSetNav
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		vendorId: [String, Number],
		integrationId: [String, Number],
		page: Number,
		countPerPage: Number
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: '',
				dateRange: {
					// object with date obj
					minDatetime: formatDate(
						new Date(sub(new Date(), { weeks: 1 }))
					),
					maxDatetime: formatDate(new Date())
				},
				changedByUserId: undefined,
				SourceApplication: 'VueSupport',
				entityType: 'Integrators', //DEv: change by real integrations entityType
				vendorId: this.$route.params.vendorId,
				integrationId: this.$route.params.integrationId,
				changedEntityId: this.$route.params.integrationId,
				page: 1,
				countPerPage: 10
			},
			loading: false,
			entityParams: {
				entityType: entityTypes,
				changedEntityId: this.id
			},
			fieldChanges: {},
			displayRowDetail: false,
			headerOptions: [
				{
					key: 'timestamp',
					label: 'Date/Time',
					sorteable: true
				},
				{
					key: 'action',
					label: 'Action',
					sorteable: false
				},
				{
					key: 'changedByAndRole',
					label: 'Changed By - Role',
					sorteable: true
				}
			],
			integrationLogs: []
		};
	},
	async created() {
		this.mainNavActions = this.actions;
		this.tableSource.headers = this.headerOptions;

		this.loading = true;
		try {
			await this.getUsers({
				changedEntityId: this.changedEntityId,
				entityType: 'IntegrationsModel'
			});
			this.vendor = this.integrator;
		} finally {
			this.loading = false;
		}
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.INTEGRATION_HISTORY);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	computed: {
		...mapState('historyLogs', ['historyLogs', 'users']),
		hasResults() {
			return this.historyLogs.items?.length > 0;
		},
		resultCounter() {
			return this.historyLogs.items?.length || 0;
		},
		urlNames() {
			let breadcrumbs = _.get(
				this,
				'$route.meta.breadcrumb',
				this.$store.getters.breadcrumbs(this.$route.path)
			);
			if (
				breadcrumbs.length > 0 &&
				breadcrumbs[breadcrumbs.length - 1].name !==
					'3P Vendor Integration History'
			) {
				breadcrumbs.push({ name: '3P Vendor Integration History' });
			}
			return breadcrumbs;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		}
	},
	methods: {
		...mapActions('historyLogs', ['getHistoryLogs', 'getUsers']),
		async fetchData() {
			this.loading = true;

			let query = removeObjectEmptyValues(this.filters);

			const filteredQuery = _.cloneDeep(query);

			filteredQuery.recordStart = this.filters.page;
			filteredQuery.maxCount = this.filters.countPerPage;

			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			filteredQuery.minDatetime = this.filters.dateRange.minDatetime;
			filteredQuery.maxDatetime = this.filters.dateRange.maxDatetime;

			delete filteredQuery.dateRange;

			try {
				await this.getHistoryLogs(filteredQuery);
				if (!this.pageTitle) {
					this.pageTitle = this.historyLogs.pageTitle;
					this.navbarTitle = this.historyLogs.navbarTitle;
				}

				if (_.get(this.historyLogs, 'items', []).length > 0) {
					this.integrationLogs = [...this.historyLogs.items];
				}
				this.tableSource = {
					...this.tableSource,
					source: this.historyLogs
				};
			} finally {
				this.loading = false;
			}
		},
		showRowDetail(index) {
			this.indexLogSeleted = index;

			this.fieldChanges = this.tableSource.source.items[
				this.indexLogSeleted
			];

			this.displayRowDetail = true;
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let { vendorId, integrationId } = this.$route.params;

			this.$router
				.push({
					path: `/vendors/${vendorId}/integrations/history/${integrationId}`,
					query: queries
				})
				.catch(() => {});

			this.fetchData();
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
		},
		updatedPagination(value) {
			this.filters.page = value.page;
			this.filters.countPerPage = value.pageSize;
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.updateRoute();
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (_.get(to, 'meta.breadcrumb.length', 0) === 0) {
			to.meta.breadcrumb = [...this.urlNames];
		}

		next();
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Vendors Integrations History Screen
    * Can use SCSS variables = TRUE
    *
**/
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 250px;
	}
}
</style>

<style lang="scss">
/*------------------------------------*\
  Vendors Integration History Table
\*------------------------------------*/

.table-container--vendors-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		@include mq($from: md) {
			&--date-time,
			&--role,
			&--changed-by {
				width: 85px;
			}
			&--action {
				width: 175px;
			}
		}
	}
}

/*------------------------------------*\
  Vendors Integrations History Table - Data
\*------------------------------------*/

.vendors-history-data {
	&__text-emphasis {
		@include body-text('regular-emphasis');
		margin-top: $gutter-sm;
	}

	&__action {
		display: flex;
		justify-content: space-between;
		gap: $gutter-xl;
		align-items: center;

		&-text {
			text-transform: initial;
		}
	}
}
</style>
